import React, { useCallback } from "react";
import {
  calculateMultiPropLineItemComparisonValueAndSelections,
  calculateSumForComparisonGroup
} from "pages/lenderReporting/drawer/line-item-management/insertNewLineButton";
import { MaterialIcon, LabelString, ModuleAccordionSection } from "@loanbossinc/component-library";
import { IconButton } from "@material-ui/core";

import { Container } from "../../dashboard/styles";

import { formattedDollarLabel } from "./noiRegularCalculationDashboard/noiRegularCalculationDashboard";
import { updateLineItemsIfNoiFieldIsUsed } from "./utils";
import {
  StyledLabel,
  StyledLabelCont,
  StyledLabelDollarFormatted,
  StyledLabelString,
  StyledLabelStringColumn,
  StyledLineItemName,
  StyledLineItemRow,
  StyledLineItemRowWrapper,
  StyledSinglePropComparison,
  StyledToggleSelect,
  StyledValue,
  SubItem,
  SubItemsWrapper,
  MultiplePropComparisonGroupHeader,
  MultiplePropComparisonGroupRow,
  MultiplePropComparisonGroupWrapper,
  MultiplePropDetailsWrapper,
  MultiplePropValueRow,
  PaddedWrapper
} from "./styles";

const toggleCumulativeOptions = [
  { name: "Cumulative", index: 0, value: "cumulative" },
  { name: "Individual", index: 1, value: "individual" }
];

export const SinglePropComparison = props => {
  const { comparisonFields, comparisonSubFieldNames, comparisonOperator } = props.lineItem;

  const comparisonOperatorValue = comparisonOperator === "greaterOf" ? "GREATER OF" : "LESSER OF";

  const determineSubFieldIndexToHighlight = useCallback(() => {
    let indexToHighlight = null;
    if (comparisonOperator === "greaterOf") {
      let greatestValue = -Infinity;
      comparisonFields.forEach((field, index) => {
        if (field.value >= greatestValue) {
          greatestValue = field.value;
          indexToHighlight = index;
        }
      });
    } else {
      let smallestValue = Infinity;
      comparisonFields.forEach((field, index) => {
        if (field.value <= smallestValue) {
          smallestValue = field.value;
          indexToHighlight = index;
        }
      });
    }

    return indexToHighlight;
  }, [comparisonFields, comparisonOperator]);

  return (
    <StyledSinglePropComparison>
      <StyledLabelCont>
        <StyledLabel>
          <StyledLabelStringColumn value={comparisonOperatorValue} />
        </StyledLabel>
      </StyledLabelCont>
      <SubItemsWrapper>
        {comparisonFields &&
          comparisonFields.map((item, idx) => {
            const toHighlight = determineSubFieldIndexToHighlight() === idx;

            return (
              <SubItem key={item.id}>
                <StyledLabelString value={comparisonSubFieldNames[idx]} lines={1} $ishighlighted={toHighlight} $bold={toHighlight} />
                <StyledLabelDollarFormatted value={item.value || ""} decimals={2} $ishighlighted={toHighlight} $bold={toHighlight} />
              </SubItem>
            );
          })}
      </SubItemsWrapper>
    </StyledSinglePropComparison>
  );
};

export const LineItemHeader = props => {
  const { lineItem, handleLineItemEdit, handleOpenLineItemDeleteDialog } = props;
  const { lineType, name, amount, isComparison, isMultipleProperties, operator } = lineItem;

  return (
    <StyledLineItemRowWrapper>
      <StyledLineItemRow isMultipleProperties={isMultipleProperties} isComparison={isComparison}>
        <Container>
          <div {...props.handleProps}>
            <IconButton disabled disableRipple style={{ color: "#0F9AD6", left: 0 }}>
              <MaterialIcon iconName="drag_indicator" />
            </IconButton>
          </div>
          <IconButton style={{ color: "#C4C4C4" }} onClick={() => handleLineItemEdit(lineItem)}>
            <MaterialIcon iconName="create" />
          </IconButton>
          <IconButton style={{ color: "#C4C4C4" }} onClick={() => handleOpenLineItemDeleteDialog(lineItem)}>
            <MaterialIcon iconName="delete" />
          </IconButton>
        </Container>
        <StyledLineItemName value={name} lines={2} />
        <PaddedWrapper padding="0 0.5em">
          <LabelString value={operator === "addition" ? "+" : "-"} lines={1} />
        </PaddedWrapper>
        <StyledValue isMultiple={isMultipleProperties}>{formattedDollarLabel(amount, "normal")}</StyledValue>
      </StyledLineItemRow>
      {lineType === "comparison" && !isMultipleProperties && <SinglePropComparison lineItem={lineItem} />}
    </StyledLineItemRowWrapper>
  );
};

export const MultiplePropertiesRow = props => {
  const { lineItem, setNoiCalculationsDashboardSelections, lineItems, isExpanded, setExpansion, allFields } = props;
  const {
    isComparison,
    multiplePropertiesValueFields,
    multiplePropertiesComparisonFields,
    comparisonSubFieldNames,
    comparisonOperator,
    selectedGroupIndex,
    selectedPropertiesIndices,
    comparisonCalculationType
  } = lineItem;

  const sectionsContent = () => {
    if (isComparison) {
      if (!multiplePropertiesComparisonFields) {
        return null;
      }

      const comparisonOperatorValue = comparisonOperator === "greaterOf" ? "GREATER OF" : "LESSER OF";

      const isHighlightedPropertyRow = (groupIndex, propertyIndex) => comparisonCalculationType === "individual" && selectedPropertiesIndices && selectedPropertiesIndices[groupIndex].includes(propertyIndex);

      const handleCumulativeChange = option => {
        const selected = option || toggleCumulativeOptions[0];
        // update line item
        const { totalValue, selectedGroupIndex, selectedPropertiesIndices } = calculateMultiPropLineItemComparisonValueAndSelections(
          selected.value,
          comparisonOperator,
          multiplePropertiesComparisonFields
        );

        const updatedLineItem = { ...lineItem, comparisonCalculationType: selected.value, amount: totalValue, selectedGroupIndex, selectedPropertiesIndices };
        const updatedLineItems = [...lineItems.filter(li => li !== lineItem), updatedLineItem];

        const updatedWithNoi = updateLineItemsIfNoiFieldIsUsed(updatedLineItems, allFields);
        setNoiCalculationsDashboardSelections({ lineItems: updatedWithNoi });
      };

      return (
        <MultiplePropDetailsWrapper style={{ position: "relative" }}>
          <StyledToggleSelect
            options={toggleCumulativeOptions}
            onChange={handleCumulativeChange}
            defaultIndex={comparisonCalculationType === "cumulative" ? 0 : 1}
            value={comparisonCalculationType === "cumulative" ? toggleCumulativeOptions[0] : toggleCumulativeOptions[1]}
          />
          {multiplePropertiesComparisonFields.map((group, groupIndex) => (
            <MultiplePropComparisonGroupWrapper key={groupIndex}>
              <MultiplePropComparisonGroupHeader>
                <StyledLabelString
                  value={comparisonSubFieldNames[groupIndex]}
                  lines={1}
                  $ishighlighted={comparisonCalculationType === "cumulative" && selectedGroupIndex === groupIndex}
                  $bold
                  $size="14px"
                />
                <StyledLabelDollarFormatted
                  value={calculateSumForComparisonGroup(multiplePropertiesComparisonFields[groupIndex])}
                  decimals={2}
                  $bold
                  $ishighlighted={comparisonCalculationType === "cumulative" && selectedGroupIndex === groupIndex}
                />
              </MultiplePropComparisonGroupHeader>
              {group.map((row, index) => (
                <MultiplePropComparisonGroupRow key={row.propertyId}>
                  <StyledLabelString value={row.propertyName} lines={1} $ishighlighted={isHighlightedPropertyRow(groupIndex, index)} />
                  <>
                    {row.selectedField && row.selectedField?.value !== null ? (
                      <StyledLabelDollarFormatted
                        value={row.selectedField?.value}
                        decimals={2}
                        $ishighlighted={isHighlightedPropertyRow(groupIndex, index)}
                      />
                    ) : (
                      <div>None selected</div>
                    )}
                  </>
                </MultiplePropComparisonGroupRow>
              ))}
            </MultiplePropComparisonGroupWrapper>
          ))}
          <StyledLabelCont pl="1.6em" style={{ position: "absolute", height: "80%", top: "20%" }}>
            <StyledLabel>
              <StyledLabelStringColumn value={comparisonOperatorValue} />
            </StyledLabel>
          </StyledLabelCont>
        </MultiplePropDetailsWrapper>
      );
    }

    return (
      <MultiplePropDetailsWrapper>
        {multiplePropertiesValueFields &&
          multiplePropertiesValueFields.map(propField => {
            const value =
              propField.selectedField && propField.selectedField?.value !== null ? (
                <StyledLabelDollarFormatted value={propField.selectedField?.value} decimals={2} />
              ) : (
                <div>None selected</div>
              );

            return (
              <MultiplePropValueRow key={propField.propertyId}>
                <>{propField.propertyName}</>
                <>{value}</>
              </MultiplePropValueRow>
            );
          })}
      </MultiplePropDetailsWrapper>
    );
  };

  return (
    <ModuleAccordionSection
      border="none"
      isExpandable
      isExpanded={isExpanded}
      onToggleExpand={setExpansion}
      sections={[sectionsContent()]}
      isCustomSummary
      customContent={<LineItemHeader {...props} />}
      expandButtonPosition="right"
    />
  );
};
